import { ZAttribute } from "src/types/ZAttribute";
import { saveTranslation } from "src/common/api/apiTranslation";
import { notification } from "antd";
import { ZUid } from "src/types/zUid";
import {
  AttributeO2,
  CommonNodeO2,
  GroupO2,
  ZLightGroup,
  newItemId,
  visitO2,
  zLightGroup,
} from "../Obj2Nodes";
import { findNodeOwnerByKey } from "../../../../common/findNodeOwnerByKey";
import {
  createAttrInGroup,
  createAttrInValue,
  createAttributeInObject,
  createGroupInGroup,
  createGroupInObj,
  createGroupInValue,
  deleteRestrictions,
  saveRestrictions,
  updateAttribute,
  updateGroup,
} from "../../objectsApi";
import { EdAttribute, edit2attr } from "../EdAttribute";
import {
  saveAttributePermissions,
  savePermissionOnMnemonicGroup,
} from "../roles/rolesApi";
import { EdGroup } from "../EdGroup";

const savePermissionOnMnemonicGroupList = async (
  attributeId: ZUid,
  stateIdList: ZUid[],
) => {
  if (!stateIdList.length) {
    return;
  }
  await Promise.all(
    stateIdList.map((stateId) =>
      savePermissionOnMnemonicGroup(attributeId, stateId),
    ),
  )
    .then(() => {
      notification.success({
        message: "Перенос выполнен",
      });
    })
    .catch(() => {
      notification.error({
        message: "Перенос не выполнен",
      });
    });
};

/**
 * Владелец сохраняемой сущности: объект, группа или значение справочника.
 * Выделен из узла дерева, чтобы сохранение не зависело от того, как устроено
 * дерево у конкретной версии вкладки.
 */
export type SaveOwner =
  | { kind: "group"; groupId: ZUid }
  | { kind: "object"; objectId: ZUid }
  | { kind: "value"; groupId: ZUid; valueId: ZUid };

const ownerOfNode = (owner: CommonNodeO2): SaveOwner =>
  visitO2<SaveOwner>(owner, {
    attr: () => {
      throw Error("Атрибут не может быть владельцем");
    },
    obj: (it) => ({ kind: "object", objectId: it.object.id }),
    group: (it) => ({ kind: "group", groupId: it.group.id }),
    value: (it) => ({
      kind: "value",
      groupId: it.groupId,
      valueId: it.value.id,
    }),
  });

// Перехватывать ошибки не надо, т.к. это происохдит в ModelessForm

/** Сохранение атрибута у известного владельца. */
export const saveAttrToOwner = async (
  owner: SaveOwner,
  srcNodeAttr: ZAttribute,
  values: EdAttribute,
): Promise<ZAttribute> => {
  const oldName = srcNodeAttr.name;
  const {
    attr: validValues,
    restrictions,
    rolesMap,
    translations = {},
    config,
    copyStatesForMnemonicGroup = [],
  } = edit2attr(values);
  const srcAttr: ZAttribute = { ...srcNodeAttr, ...validValues };
  const { id, ...otherFields } = srcAttr;
  const isNewAttr = id === newItemId;
  const createIn = async (): Promise<ZAttribute> => {
    switch (owner.kind) {
      case "object":
        return createAttributeInObject(owner.objectId, otherFields, config);
      case "group":
        return createAttrInGroup(owner.groupId, otherFields, config);
      // Атрибут значения справочника: POST /groups/{id}/values/{valueId}/attributes
      default:
        return createAttrInValue(
          owner.groupId,
          owner.valueId,
          otherFields,
          config,
        );
    }
  };
  const dstAttr = isNewAttr
    ? await createIn()
    : await updateAttribute(srcAttr, config);
  if (restrictions) {
    await saveRestrictions(dstAttr.id, restrictions);
  } else {
    await deleteRestrictions(dstAttr.id);
  }
  await Promise.all([
    ...Object.entries(rolesMap).map(([stateId, permissions]) =>
      saveAttributePermissions(dstAttr.id, stateId, permissions),
    ),
    saveTranslation(oldName, {
      value: dstAttr.name,
      translations,
    }),
    savePermissionOnMnemonicGroupList(dstAttr.id, copyStatesForMnemonicGroup),
  ]);
  return dstAttr;
};

export const commonAttrSave = async (
  attrNode: AttributeO2,
  values: EdAttribute,
  treeData: CommonNodeO2[],
): Promise<{
  owner: CommonNodeO2;
  attr: ZAttribute;
}> => {
  const res = findNodeOwnerByKey(attrNode.key, treeData);
  if (!res) {
    throw Error("Не найден владелец атрибута");
  }
  const attr = await saveAttrToOwner(
    ownerOfNode(res.owner),
    attrNode.attr,
    values,
  );
  return { attr, owner: res.owner };
};

/** Сохранение группы у известного владельца. */
export const saveGroupToOwner = async (
  owner: SaveOwner,
  srcNodeGroup: ZLightGroup,
  values: unknown,
): Promise<ZLightGroup> => {
  const oldName = srcNodeGroup.name;
  const { translations, ...groupValues } = values as EdGroup;
  const validValues = zLightGroup.partial().parse(groupValues);
  const srcGroup = { ...srcNodeGroup, ...validValues };
  const { id, ...otherFields } = srcGroup;
  const isNewGroup = id === newItemId;
  const createIn = async (): Promise<ZLightGroup> => {
    switch (owner.kind) {
      case "object":
        return createGroupInObj(owner.objectId, otherFields);
      case "group":
        return createGroupInGroup(owner.groupId, otherFields);
      default:
        return createGroupInValue(owner.groupId, owner.valueId, otherFields);
    }
  };
  const result = isNewGroup ? await createIn() : await updateGroup(srcGroup);
  await saveTranslation(oldName, {
    value: result.name,
    translations,
  });
  return result;
};

export const commonGroupSave = async (
  groupNode: GroupO2,
  values: unknown,
  treeData: CommonNodeO2[],
): Promise<ZLightGroup> => {
  const res = findNodeOwnerByKey(groupNode.key, treeData);
  if (!res) {
    throw Error("Не найден владелец группы");
  }
  return saveGroupToOwner(ownerOfNode(res.owner), groupNode.group, values);
};
